import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Minimum scroll delta (in pixels) required to register a direction change.
 * Prevents micro-changes from triggering direction updates.
 * @type {number}
 */
const SCROLL_THRESHOLD = 10;

/**
 * Hook que detecta la dirección de scroll del usuario.
 * Usa requestAnimationFrame para throttle y un threshold de 10px
 * para evitar micro-cambios de dirección.
 *
 * @returns {string|null} scrollDirection - 'up', 'down', or null (initial state)
 */
export const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollY = useRef(0);
  const rafId = useRef(null);

  const handleScroll = useCallback(() => {
    if (rafId.current !== null) {
      return;
    }

    rafId.current = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      if (Math.abs(delta) >= SCROLL_THRESHOLD) {
        setScrollDirection(delta > 0 ? 'down' : 'up');
        lastScrollY.current = currentScrollY;
      }

      rafId.current = null;
    });
  }, []);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [handleScroll]);

  return scrollDirection;
};
